import type { UIOfferResponseResource } from '~/components/guest-checkout/helpers'
import type { PayoutType } from '~/types/offer'

// Since the "instant product" backend change, GET product/offer wraps offers in a
// productTypes envelope: { productTypes: [{ productType, assetGroups: [...] }] }.
// Regular challenges live under productType "OFFER_PRODUCT", instant funding under
// "INSTANT_PRODUCT".
export const OFFER_PRODUCT_TYPE = 'OFFER_PRODUCT'
export const INSTANT_PRODUCT_TYPE = 'INSTANT_PRODUCT'

export type OffersProductTypeName = typeof OFFER_PRODUCT_TYPE | typeof INSTANT_PRODUCT_TYPE

export type OffersProductType = {
  productType: string
  assetGroups: UIOfferResponseResource[]
}

export type OffersResponse = {
  productTypes: OffersProductType[]
}

// What GET product/offer can actually hand back: the productTypes envelope, or - if the
// backend is rolled back - the bare asset-group array it used to return.
export type OffersApiResponse = OffersResponse | UIOfferResponseResource[]

// Extracts the asset-group array (each holding payoutTypes) for a single product type
// out of the envelope. Falls back to the value as-is when the backend still returns the
// old array shape, so a backend rollback does not re-break the page - that legacy shape
// only ever carried the regular offers, so instant reads come back empty.
export function extractProductGroups(
  response: OffersResponse | UIOfferResponseResource[] | null | undefined,
  productType: OffersProductTypeName = OFFER_PRODUCT_TYPE,
): UIOfferResponseResource[] {
  if (!response) return []
  if (Array.isArray(response)) return productType === OFFER_PRODUCT_TYPE ? response : []

  const product = response.productTypes?.find((entry) => entry.productType === productType)

  return product?.assetGroups ?? []
}

export function extractOfferProductGroups(
  response: OffersResponse | UIOfferResponseResource[] | null | undefined,
): UIOfferResponseResource[] {
  return extractProductGroups(response, OFFER_PRODUCT_TYPE)
}

// Futures and Stocks are queried per assetType, so each product type holds a single
// asset group. This unwraps it straight to the payout types the decision tree needs.
export function extractPayoutTypes(
  response: OffersResponse | UIOfferResponseResource[] | null | undefined,
  productType: OffersProductTypeName = OFFER_PRODUCT_TYPE,
): PayoutType[] {
  return extractProductGroups(response, productType)[0]?.payoutTypes ?? []
}

// Whether a product type actually has something to sell. Drives the Prime | Instant
// selector: the tab shows up because the payload carries an instant product with
// offers, not because a particular route opted in.
export function hasAvailableOffers(payoutTypes: PayoutType[] | null | undefined): boolean {
  return Boolean(
    payoutTypes?.some((payoutType) => payoutType.evaluationTypes?.some((evaluation) => evaluation.offers?.length > 0)),
  )
}
